/**
 * Twilio Voice SDK quality monitoring helpers — shared by every
 * consumer that owns a Twilio call object (ActiveCallOverlay and any
 * future non-shared-bus panels).
 *
 * Wires `call.on('sample', ...)`, `call.on('warning', ...)`, and
 * `call.on('warning-cleared', ...)` to:
 *   1. Maintain a `connectionQuality` traffic-light state (good/fair/poor).
 *   2. Forward degraded-call telemetry to the backend so we can
 *      identify patterns ("are calls bad on Mondays?", "is one user
 *      always affected?", etc.) instead of guessing.
 *
 * Twilio's documented thresholds (built into the SDK):
 *   - high-rtt:         RTT > 400 ms in 3 of last 5 samples
 *   - low-mos:          MOS < 3.5  in 3 of last 5 samples
 *   - high-jitter:      jitter > 30 ms in 3 of last 5 samples
 *   - high-packet-loss: loss > 1%  in 3 of last 5 samples
 */

import { twilioAPI } from "@/services/api";

/**
 * Voice SDK 2.x Device options that give the best out-of-the-box voice
 * quality for healthcare/call-center workloads:
 *   - Opus preferred over PCMU (already there)
 *   - Wideband Opus at the upper end of Twilio's recommended speech range
 *   - `enableRingingState: true` so we get an explicit RINGING event
 *   - `closeProtection: true` so the browser confirms before closing
 *     mid-call (no more "tab closed and cut the call")
 *   - `edge: "roaming"` is the SDK default in 2.x, so we DON'T set it
 *     explicitly (manually setting an edge disables Twilio's automatic
 *     low-latency routing).
 */
export const RECOMMENDED_DEVICE_OPTIONS = {
  codecPreferences: ["opus", "pcmu"],
  enableRingingState: true,
  closeProtection: true,
  // Twilio's docs: valid range 6,000–510,000 bps; speech recommendation
  // 8,000–40,000. 40k = top of the speech recommendation = clearest
  // possible voice without bloating bandwidth on cell connections.
  maxAverageBitrate: 40000,
};

/**
 * Explicit getUserMedia constraints that produce dramatically clearer
 * voice than the browser's defaults (which vary by vendor and version).
 *
 * The three "RTC voice DSP" flags — echoCancellation, noiseSuppression,
 * autoGainControl — fix the three classes of problems users report most:
 *   echo / hearing yourself, background noise / fan hum, and uneven volume.
 *
 * Mono + 48 kHz matches what Opus actually encodes, so we avoid an extra
 * downsample step inside the browser audio pipeline.
 */
export const RECOMMENDED_MIC_CONSTRAINTS = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1,
    sampleRate: 48000,
    sampleSize: 16,
  },
  video: false,
};

/**
 * Map an instantaneous sample → traffic-light label.
 * Based on Twilio's own warning thresholds, with a `fair` middle band so
 * users see *something* before things go fully red.
 */
function gradeSample(sample) {
  const rtt = Number(sample?.rtt || 0);
  const jitter = Number(sample?.jitter || 0);
  const lossFraction = Number(sample?.packetsLostFraction || 0);
  const mos = Number(sample?.mos || 5);

  // Poor — Twilio's own threshold values.
  if (rtt > 400 || jitter > 30 || lossFraction > 0.01 || mos < 3.5) return "poor";
  // Fair — halfway to the threshold; gives a heads-up before things fail.
  if (rtt > 250 || jitter > 18 || lossFraction > 0.003 || mos < 4.0) return "fair";
  return "good";
}

/**
 * Wire quality listeners onto a Twilio Voice Call instance.
 *
 * @param {Call} call - the @twilio/voice-sdk Call object
 * @param {object} opts
 * @param {(quality: 'good'|'fair'|'poor') => void} opts.onQualityChange
 *        Called whenever the rolling grade changes.
 * @param {(message: string, warningName: string) => void} [opts.onWarning]
 *        Called when Twilio raises a quality warning. Use to show a banner.
 * @param {() => void} [opts.onWarningCleared]
 *        Called when all active warnings clear. Use to hide the banner.
 * @returns {() => void} cleanup — call to detach listeners.
 */
export function attachCallQualityListeners(call, opts = {}) {
  if (!call || typeof call.on !== "function") return () => {};
  const { onQualityChange, onWarning, onWarningCleared } = opts;

  let lastGrade = "good";
  const activeWarnings = new Set();
  // Buffer the last few sample readings for the optional debug overlay.
  const rolling = [];
  // Throttle backend telemetry — we don't need 1 POST/sec per call.
  // Send only on grade transitions + warning events.
  let lastReportedGrade = "good";

  const sampleHandler = (sample) => {
    rolling.push({
      t: Date.now(),
      rtt: sample.rtt,
      jitter: sample.jitter,
      mos: sample.mos,
      loss: sample.packetsLostFraction,
    });
    if (rolling.length > 30) rolling.shift();

    const grade = gradeSample(sample);
    if (grade !== lastGrade) {
      lastGrade = grade;
      onQualityChange?.(grade);
    }

    // Only ship telemetry to backend when grade *worsens* — `good→fair`,
    // `fair→poor`, or `good→poor`. No-ops on recovery (we already get a
    // `warning-cleared` event for that).
    const sev = { good: 0, fair: 1, poor: 2 };
    if (sev[grade] > sev[lastReportedGrade]) {
      lastReportedGrade = grade;
      void reportQualityEvent(call, "grade-degraded", {
        grade,
        sample: { rtt: sample.rtt, jitter: sample.jitter, mos: sample.mos, loss: sample.packetsLostFraction },
      });
    }
  };

  const warningHandler = (warningName, warningData) => {
    activeWarnings.add(warningName);
    const message = WARNING_MESSAGES[warningName] || `Network issue: ${warningName}`;
    onWarning?.(message, warningName);
    void reportQualityEvent(call, "warning", { warningName, warningData });
  };

  const warningClearedHandler = (warningName) => {
    activeWarnings.delete(warningName);
    if (activeWarnings.size === 0) onWarningCleared?.();
  };

  call.on("sample", sampleHandler);
  call.on("warning", warningHandler);
  call.on("warning-cleared", warningClearedHandler);

  // Best-effort detach. The Voice SDK's `call.off` was added in v2.6+,
  // so this should always work on 2.18.
  return () => {
    try { call.off?.("sample", sampleHandler); } catch {}
    try { call.off?.("warning", warningHandler); } catch {}
    try { call.off?.("warning-cleared", warningClearedHandler); } catch {}
  };
}

const WARNING_MESSAGES = {
  "high-rtt": "Network latency is high — callers may notice a delay.",
  "low-mos": "Call quality is degraded — try moving closer to Wi-Fi or switching networks.",
  "high-jitter": "Network jitter detected — audio may sound choppy.",
  "high-packet-loss": "Packet loss detected — audio may cut out.",
  // Twilio SDK 2.x also fires these — previously fell through to raw name.
  "low-bytes-received": "Not receiving audio from the network — check your Wi-Fi/internet.",
  "low-bytes-sent": "Your audio isn't reaching the network — check your Wi-Fi/internet.",
  "constant-audio-input-level": "Microphone appears muted or unavailable — check your mic.",
  "no-audio-input": "No sound from your microphone — check your input device.",
};

/**
 * POST a single quality event to the backend.
 * Silent on failure — we never want quality reporting to interrupt a call.
 */
async function reportQualityEvent(call, eventType, payload) {
  try {
    const callSid =
      call?.parameters?.CallSid ||
      call?.outboundConnectionId ||
      null;
    await twilioAPI.reportCallQuality({
      call_sid: callSid,
      event_type: eventType,
      payload,
    });
  } catch (e) {
    // Don't toast. Don't throw. Silent best-effort.
    if (typeof window !== "undefined" && window.localStorage?.getItem?.("debug_call_quality")) {
      // eslint-disable-next-line no-console
      console.warn("[call-quality] report failed:", e?.message);
    }
  }
}
